import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Icon from 'react-web-vector-icons';
import PageBase from "../../../components/PageBase/index";
import Datatable from "../../../components/Datatable/index";
import * as userActions from "../../../redux/User/actions";
import LoadingPanel from "../../../components/LoadingPanel/index";
import columns from './column.js';
import PopupWin from "../../../components/PopupWin/index";
import PopupButton from "../../../components/PopupButton";
import CreateExternalUser from "./CreateExternalUser/index";
import CreateInternalUser from "./CreateInternalUser/index";
import Button from "../../../components/Button";
import Toaster from "../../../components/Toaster";
import useColumnFilters from '../../../hooks/useColumnFilters.js';
import './Users.scss';

const Users = props => {
	const [showCreateExternalUserPopup, setShowCreateExternalUserPopup] = useState(false);
	const [showCreateInternalUserPopup, setShowCreateInternalUserPopup] = useState(false);
	const [loading, setLoading] = useState(true);
	const [createInternalUserFunc, setCreateInternalUserFunc] = useState(null);
	// const [filterResetKey, setFilterResetKey] = useState(false);
	const dispatch = useDispatch();
	const user = useSelector(state => state.user).user;
	const users = useSelector(state => state.user).users;

	const accounts = [ ...user.customers];
	const availableAccounts = accounts
		.sort((a,b) => a.Name.localeCompare(b.Name))
		.map(account => ({ value: account.Name.trim(), label: account.Name.trim() }));

	const {
		filters,
		sortBy,
		sortOrder,
		filteredData,
		handleFilterChange,
		resetWith,
		updateData,
		uniqueValuesByColumn,
		filterOrder
	} = useColumnFilters({
		data: users,
		columns,
		storageKey: `users`,
		initialState: {}
	});
	useEffect(() => {
		const load = async () => {
			await dispatch(userActions.loadUsers());
			setLoading(false);
		};
		load();
	}, [dispatch]);
		useEffect(() => {
		updateData(users);
	}, [users]);

	// useEffect(() => {
	// 	setFilterResetKey(prev => prev + 1);
	// }, [search, selectedFilters]);

	if(loading) {
		return (
			<PageBase title="User Management" removeMargins>
				<LoadingPanel />
			</PageBase>
		)
	}



	const changeUserDatabase = async (email, database) => {
		setLoading(true);
		await dispatch(userActions.changeUserDatabase(email, database, afterChangeDb));
		setLoading(false);
	}

	const afterChangeDb = (result) => {
	    if(result.status === 'error' && result.message === 'no-sandbox') {
			Toaster.showWarn("Sandbox database does not exist.")
	    }
	}

	const refreshData = async () => {
		setLoading(true);
		await dispatch(userActions.loadUsers());
		setLoading(false);
	};

	const afterUserCreate = async () => {
		setShowCreateExternalUserPopup(false);
		setShowCreateInternalUserPopup(false);
		Toaster.showSuccess("User created successfully.");
		refreshData();
	};

	const getSortedUsers = () => {
		if(sortBy === "last_login") {
			return users.sort((a, b) =>  sortOrder === "desc" ? new Date(a.last_login) - new Date(b.last_login) : new Date(b.last_login) - new Date(a.last_login));
		}
		else if(sortBy === "registration_date") {
			return users.sort((a, b) =>  sortOrder === "desc" ? new Date(a.registration_date) - new Date(b.registration_date) : new Date(b.registration_date) - new Date(a.registration_date));
		}
		else if(sortBy === "is_enabled" || sortBy === "is_external") {
			return users.sort((a, b) =>  sortOrder === "desc" ? a.is_enabled - b.is_enabled : b.is_enabled - a.is_enabled);
		}

		return users.sort((a, b) =>  sortOrder === "asc" ? (a[sortBy] || '').localeCompare((b[sortBy] || '')) : (b[sortBy] || '').localeCompare((a[sortBy] || '')));
	};

	const toggleEnabled = async (email) => {
		setLoading(true);
		await dispatch(userActions.toggleUserEnabled(email));
		setLoading(false);
	};

	const renderUserDatabase = (row) => {
		const usingLiveDb = row.cloud_db_name == null;
		return (
			<div style={{display: 'flex', alignItems: 'center'}}>
				{
					usingLiveDb ?
						<>
							<div style={{width: '80px'}}>Live</div>
							<div onClick={() => changeUserDatabase(row.email, 'sandbox')} className="users-grid-link">Switch to Sandbox</div>
						</>
						:
						<>
							<div style={{width: '80px'}}>Sandbox</div>
							<div onClick={() => changeUserDatabase(row.email, 'live')} className="users-grid-link">Switch to Live</div>
						</>

				}
			</div>
		);
	}

	const renderIsEnabled = (row) => {
		return (
			<div style={{display: 'flex', alignItems: 'center'}}>
				<div style={{width: '70px'}}>
					{
						row.is_enabled ?
							<span style={{color: '#16cb1d'}}>Enabled</span>
							:
							<span style={{color: '#9E9E9E'}}>Disabled</span>
					}
				</div>
				<div
					onClick={() => toggleEnabled(row.email)}
					style={{color: 'blue', textDecoration: 'underline', marginLeft: '10px', fontSize: '12px', cursor: 'pointer'}}>{row.is_enabled ? 'Disable' : 'Enable'}</div>
			</div>
		);
	};

	const index = columns.findIndex(c => c.id === 'is_enabled');
	columns[index].cell = renderIsEnabled;

	const index2 = columns.findIndex(c => c.id === 'cloud_db_name');
	columns[index2].cell = renderUserDatabase;

	const sortedUsers = getSortedUsers();

	return (
		<PageBase title="User Management" removeMargins>
			<PopupWin
				className="users-popup"
				closeOnEscape={false}
				closeOnDocumentClick={false}
				visible={showCreateExternalUserPopup}
				onClose={() => setShowCreateExternalUserPopup(false)}
				title="Create External User Invitation"
				contentStyle={{width: '100%', height: '100%'}}
				popupStyle={{width: '700px', height: '600px', overflow: 'none'}}
				footer={
					<PopupButton
						variant="secondary"
						onClick={() => setShowCreateExternalUserPopup(false)}
					>
						Cancel
					</PopupButton>
				}>
				<CreateExternalUser availableAccounts={availableAccounts} onClose={afterUserCreate} />
			</PopupWin>

			<PopupWin
				className="users-popup"
				closeOnEscape={false}
				closeOnDocumentClick={false}
				visible={showCreateInternalUserPopup}
				onClose={() => setShowCreateInternalUserPopup(false)}
				title="Create Internal User"
				// contentStyle={{width: '100%', height: '100%'}}
				popupStyle={{width: '418px', height: '356px', overflow: 'none'}}
				footer={
					<>
						<PopupButton
							variant="secondary"
							onClick={() => setShowCreateInternalUserPopup(false)}
						>
							Cancel
						</PopupButton>
						<PopupButton
							variant="primary"
							onClick={createInternalUserFunc}
							disabled={!createInternalUserFunc}
						>
							Create User
						</PopupButton>
					</>
				}>
				<CreateInternalUser
					availableAccounts={availableAccounts}
					onClose={afterUserCreate}
					onCreateUser={(func) => setCreateInternalUserFunc(() => func)}
				/>
			</PopupWin>

			<div style={{height: '80px', display: 'flex', alignItems: 'center', marginLeft: '30px', columnGap: '20px' }}>
				<Button leftFontIcon={<Icon name="pluscircleo" font="AntDesign" color="white" size={17}/>} onClick={() => setShowCreateExternalUserPopup(true)} type="blue">&nbsp;&nbsp;Create External User&nbsp;</Button>
				<Button leftFontIcon={<Icon name="pluscircleo" font="AntDesign" color="white" size={17}/>} onClick={() => setShowCreateInternalUserPopup(true)} type="blue">&nbsp;&nbsp;Create Internal User&nbsp;</Button>
			</div>

			<div style={{margin: '0 30px'}}>
				<Datatable 
					gridHeight="calc(100vh - 185px)"
					columns={columns}
					data={filteredData}
					//onSort={updateSort}
					sortBy={sortBy}
					sortOrder={sortOrder}
					filters={filters}
					onFilterChange={handleFilterChange}
					filterOrder={filterOrder}
					uniqueValuesByColumn={uniqueValuesByColumn}
					allData={users}
				/>
			</div>
		</PageBase>
	)
};

export default Users;